import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, DollarSign } from 'lucide-react';
import { api } from '@/lib/api/client';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import type {
  CoCompany,
  User,
  XkBrand,
  XkEvent,
  XkEventGroup,
  XkStation,
} from '@/lib/api/types';
import {
  useCompanyManagers,
  useCurrentCompany,
} from '@/lib/branding/useCurrentBrand';
import { Skeleton } from '@/components/ui/Skeleton';
import { Pagination } from '@/components/ui/Pagination';
import { ExportXlsxButton } from '@/components/ui/ExportXlsxButton';
import { usePagination } from '@/lib/hooks/usePagination';
import { resolveImageUrl } from '@/lib/images/resolveImageUrl';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { useCurrentPlan } from '@/lib/permissions/usePlanQuota';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { BrandTreeCollapsible } from './BrandTreeCollapsible';
import { AtlediWelcomeHero } from './components/AtlediWelcomeHero';
import { EntityKpiStrip } from './components/EntityKpiStrip';
import { MyNextEventCard } from './components/MyNextEventCard';
// Caso 14 — first-event onboarding checklist; self-hides when complete.
import { FirstStepsChecklist } from '@/features/onboarding/FirstStepsChecklist';
// Caso 14 v1 — modal-based guided wizard. Auto-opens once for new accounts;
// re-openable via the checklist's "Start setup" CTA.
import { FirstEventWizard } from '@/features/onboarding/FirstEventWizard';

/**
 * Home — workspace landing surface (CompanyHomeLayout pattern).
 *
 * The Home shows the workspace as a brand portfolio (rollup + sub-brands +
 * managers). Drill-in to the agency's operational view (live events, quick
 * actions) lives at `/agency/:id` and is reached from the TopBar pill.
 */
export function DashboardPage() {
  const { company, isLoading } = useCurrentCompany();
  if (isLoading) {
    return (
      <div className="page">
        <Skeleton className="mb-4 h-9 w-72 rounded" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }
  if (!company) return null;
  return <CompanyHome company={company} />;
}

// ════════════════════════════════════════════════════════════════════════════
// AgencyHome — operational view of an agency workspace. Exported for the
// /agency/:id route. Clients on the left, live event feed below, quick-actions
// rail on the right. Mirrors `Workspace/Layouts/AgencyHomeLayout`.
// ════════════════════════════════════════════════════════════════════════════

export function AgencyHome({ company }: { company: CoCompany }) {
  const navigate = useNavigate();
  useFavoritable(`Agency · ${company.name}`);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { data: planUsage } = useCurrentPlan();

  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.all,
    queryFn: () => api.list<XkEventGroup>('/xk-event-group'),
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.all,
    queryFn: () => api.list<XkEvent>('/xk-event'),
  });

  const brands = brandsQ.data?.items ?? [];
  const groups = groupsQ.data?.items ?? [];
  const events = eventsQ.data?.items ?? [];

  // Gate "New brand" against the company's plan cap. brandsUsed is computed
  // from the live `/xk-company` listing because co_company_plan_usage.brandsUsed
  // is not maintained server-side (validator counts at mutation time).
  const brandsLimit = planUsage?.maxBrands ?? 0;
  const brandsUsed = brands.length;
  const brandQuotaReached = brandsUsed >= brandsLimit;

  const brandCards = useMemo(() => {
    // "Projects" = event groups owned by a brand (companyId === brand.id)
    const groupsByBrand = new Map<number, XkEventGroup[]>();
    for (const g of groups) {
      if (g.companyId == null) continue;
      const list = groupsByBrand.get(g.companyId) ?? [];
      list.push(g);
      groupsByBrand.set(g.companyId, list);
    }
    const eventsByGroup = new Map<number, XkEvent[]>();
    for (const e of events) {
      const list = eventsByGroup.get(e.eventGroupId) ?? [];
      list.push(e);
      eventsByGroup.set(e.eventGroupId, list);
    }
    return brands.map((b) => {
      const bGroups = groupsByBrand.get(b.id) ?? [];
      const bEvents = bGroups.flatMap((g) => eventsByGroup.get(g.id) ?? []);
      const liveEvents = bEvents.filter((e) => liveStatus(e) === 'Live now').length;
      return {
        brand: b,
        projects: bGroups.length,
        liveEvents,
      };
    });
  }, [brands, groups, events]);

  const liveFeed = useMemo(() => {
    const brandById = new Map(brands.map((b) => [b.id, b]));
    const groupById = new Map(groups.map((g) => [g.id, g]));
    return events
      .map((e) => {
        const status = liveStatus(e);
        if (!status) return null;
        const group = groupById.get(e.eventGroupId);
        const brand = group?.companyId != null ? brandById.get(group.companyId) : undefined;
        return {
          id: e.id,
          eventName: e.name,
          brandName: brand?.name ?? '—',
          location: e.address ?? '',
          status,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .sort((a, b) => statusOrder(a.status) - statusOrder(b.status))
      .slice(0, 8);
  }, [events, brands, groups]);

  const loadingPortfolio =
    brandsQ.isLoading || groupsQ.isLoading || eventsQ.isLoading;

  const quickActions = useMemo(() => {
    const pendingSetup = liveFeed.filter((e) => e.status === 'Setup').length;
    return [
      {
        id: 'invite',
        label: 'Invite a collaborator',
        hint: 'Settings → Team',
        onClick: () => navigate('/settings'),
      },
      {
        id: 'plan',
        label: 'Plan a new project',
        onClick: () => navigate('/projects'),
      },
      {
        id: 'review',
        label: 'Review activation setup',
        hint: pendingSetup ? `${pendingSetup} pending` : undefined,
        onClick: () => navigate('/projects'),
      },
      {
        id: 'billing',
        label: 'Open billing',
        onClick: () => navigate('/settings/billing'),
      },
    ];
  }, [liveFeed, navigate]);

  return (
    <div className="page grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
      {/* LEFT column — brands + live feed */}
      <div>
        <header className="mb-7">
          <div className="flex items-center gap-2">
            <h1
              className="mb-1 text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {company.name}
            </h1>
            <HelpHint section="dashboard" />
          </div>
          <p className="text-[13px] text-[var(--color-xk-text-muted)]">
            {brandsUsed >= 2
              ? `Brand Hub · ${brandsUsed} brands`
              : 'Agency · brand portfolio'}
          </p>
        </header>

        {/* Caso 14 — onboarding checklist (auto-hides once the workspace has
            a brand, an event and a station, or once the user dismisses it). */}
        <div className="mb-6">
          <FirstStepsChecklist />
          {/* Caso 14 v1 — auto-opens once for new accounts; the checklist
              stays as the persistent in-page reminder. */}
          <FirstEventWizard />
        </div>

        <Section title="Brands" action={
          <button
            type="button"
            onClick={() =>
              brandQuotaReached ? setUpgradeOpen(true) : navigate('/brand-studio/new')
            }
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-4 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)]"
          >
            <Plus className="h-3.5 w-3.5" />
            New brand
          </button>
        }>
          {loadingPortfolio ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
          ) : brandCards.length === 0 ? (
            <EmptyBrandsCard onCreate={() => navigate('/brand-studio/new')} />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {brandCards.map((b) => (
                <AgencyBrandCard
                  key={b.brand.id}
                  brand={b.brand}
                  projects={b.projects}
                  liveEvents={b.liveEvents}
                  onClick={() => navigate(`/brand/${b.brand.id}`)}
                />
              ))}
            </div>
          )}
        </Section>

        <Section title="Live now">
          {loadingPortfolio ? (
            <Skeleton className="h-32 w-full rounded-xl" />
          ) : liveFeed.length === 0 ? (
            <EmptyFeed />
          ) : (
            <ul className="divide-y divide-[var(--color-xk-border)]">
              {liveFeed.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="truncate text-[13px] font-bold text-[var(--color-xk-lavender)]">
                      {e.eventName}
                    </div>
                    <div className="truncate text-[11px] text-[var(--color-xk-text-muted)]">
                      {e.brandName}
                      {e.location ? ` · ${e.location}` : ''}
                    </div>
                  </div>
                  <StatusPill status={e.status} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {/* RIGHT column — quick actions rail */}
      <aside>
        <Section title="Quick actions">
          <div className="flex flex-col gap-2">
            {quickActions.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={a.onClick}
                className="flex items-center justify-between gap-3 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3.5 py-3 text-left text-[13px] text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-xk-border-strong)] hover:bg-[var(--color-xk-bg-deep)]"
              >
                <span>{a.label}</span>
                {a.hint && (
                  <span
                    className="text-[11px] text-[var(--color-xk-text-muted)] tabular-nums"
                    style={{ fontFamily: 'var(--font-display)' }}
                  >
                    {a.hint}
                  </span>
                )}
              </button>
            ))}
          </div>
        </Section>
      </aside>
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="maxBrands"
        currentPlanName="Free"
        limit={brandsLimit}
      />
    </div>
  );
}

type LiveStatus = 'Live now' | 'Setup' | 'Wrapping';

function liveStatus(e: XkEvent): LiveStatus | null {
  if (e.eventStatus !== 'Active') return null;
  if (!e.startDate || !e.endDate) return null;
  const now = Date.now();
  const start = new Date(e.startDate).getTime();
  const end = new Date(e.endDate).getTime();
  const inSetupWindow = start - now < 7 * 24 * 60 * 60 * 1000; // 7 days
  if (now >= start && now <= end) return 'Live now';
  if (now > end && now - end < 3 * 24 * 60 * 60 * 1000) return 'Wrapping';
  if (now < start && inSetupWindow) return 'Setup';
  return null;
}

function statusOrder(s: LiveStatus): number {
  return s === 'Live now' ? 0 : s === 'Setup' ? 1 : 2;
}

function AgencyBrandCard({
  brand,
  projects,
  liveEvents,
  onClick,
}: {
  brand: XkBrand;
  projects: number;
  liveEvents: number;
  onClick: () => void;
}) {
  const accent = brand.secondaryColor || 'var(--color-xk-accent)';
  return (
    // The card is a div (not a button) so the inner collapsible's trigger
    // — itself a button — isn't nested inside another button. The header
    // area below stays clickable and navigates to the brand home.
    <div className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 transition-all hover:border-[var(--color-accent-ink)]">
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center gap-3 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-xk-accent)]"
      >
        {brand.logo ? (
          <img
            src={resolveImageUrl(brand.logo)}
            alt={brand.name}
            className="h-9 w-9 flex-none rounded-md object-contain"
          />
        ) : (
          <span
            aria-hidden="true"
            className="grid h-9 w-9 flex-none place-items-center rounded-md text-sm font-extrabold text-[var(--color-fg-on-accent)]"
            style={{ backgroundColor: accent, fontFamily: 'var(--font-display)' }}
          >
            {brand.name.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <div className="truncate text-sm font-extrabold text-[var(--color-xk-lavender)]">
            {brand.name}
          </div>
          <div
            className="mt-0.5 text-[11px] tabular-nums text-[var(--color-xk-text-muted)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {projects} project{projects === 1 ? '' : 's'} · {liveEvents} live
          </div>
        </div>
      </button>
      <div className="mt-3 border-t border-[var(--color-xk-border)] pt-2.5">
        <BrandTreeCollapsible brandId={brand.id} />
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: LiveStatus }) {
  const cfg: Record<LiveStatus, { bg: string; fg: string }> = {
    'Live now': { bg: 'rgb(34 197 94 / 0.10)', fg: 'var(--color-success)' },
    Setup: { bg: 'rgb(245 158 11 / 0.10)', fg: 'var(--color-warn)' },
    Wrapping: { bg: 'var(--color-accent-10)', fg: 'var(--color-xk-accent)' },
  };
  const c = cfg[status];
  return (
    <span
      className="flex-none rounded-full px-2.5 py-1 text-[11px] font-bold"
      style={{ backgroundColor: c.bg, color: c.fg }}
    >
      {status}
    </span>
  );
}

function EmptyFeed() {
  return (
    <div className="rounded-xl border border-dashed border-[var(--color-xk-border)] p-6 text-center text-[12px] text-[var(--color-xk-text-muted)]">
      Nothing live right now. Atledi will surface live events here when they begin.
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CompanyHome — sub-brand portfolio rollup (AB InBev style).
// ════════════════════════════════════════════════════════════════════════════

const SPEND_PER_LIVE_STATION = 25_000;
const REACH_PER_STATION_YTD = 1_500;

function CompanyHome({ company }: { company: CoCompany }) {
  const navigate = useNavigate();
  const t = useT();
  useFavoritable(`${t('sidebar.home')} · ${company.name}`);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { data: planUsage } = useCurrentPlan();

  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.all,
    queryFn: () => api.list<XkEventGroup>('/xk-event-group'),
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.all,
    queryFn: () => api.list<XkEvent>('/xk-event'),
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.all,
    queryFn: () => api.list<XkStation>('/xk-station'),
  });
  const managersQ = useCompanyManagers(company.id);

  const portfolio = useMemo(() => {
    const brands = brandsQ.data?.items ?? [];
    const groups = groupsQ.data?.items ?? [];
    const events = eventsQ.data?.items ?? [];
    const stations = stationsQ.data?.items ?? [];

    // "Projects" = event groups owned by a brand
    const groupsByBrand = new Map<number, XkEventGroup[]>();
    for (const g of groups) {
      if (g.companyId == null) continue;
      const list = groupsByBrand.get(g.companyId) ?? [];
      list.push(g);
      groupsByBrand.set(g.companyId, list);
    }
    const eventsByGroup = new Map<number, XkEvent[]>();
    for (const e of events) {
      const list = eventsByGroup.get(e.eventGroupId) ?? [];
      list.push(e);
      eventsByGroup.set(e.eventGroupId, list);
    }
    const stationsByEvent = new Map<number, XkStation[]>();
    for (const s of stations) {
      const list = stationsByEvent.get(s.eventId) ?? [];
      list.push(s);
      stationsByEvent.set(s.eventId, list);
    }

    const stationsForGroup = (g: XkEventGroup) => {
      const ev = eventsByGroup.get(g.id) ?? [];
      return ev.reduce((s, e) => s + (stationsByEvent.get(e.id)?.length ?? 0), 0);
    };

    /**
     * Backend xk_event_group does NOT expose `projectStatus` — we derive it
     * from the child events' `eventStatus` (which IS authoritative):
     *   any event Live now      → group is live
     *   any event Setup/Active  → live (close enough for the rollup)
     *   else                    → wrapped/planning (not counted)
     */
    const liveStationsForGroup = (g: XkEventGroup) => {
      const ev = eventsByGroup.get(g.id) ?? [];
      return ev.reduce((sum, e) => {
        const ls = liveStatus(e);
        if (ls === 'Live now' || ls === 'Setup') {
          return sum + (stationsByEvent.get(e.id)?.length ?? 0);
        }
        return sum;
      }, 0);
    };

    const now = Date.now();
    return brands
      .map((brand) => {
        const bGroups = groupsByBrand.get(brand.id) ?? [];
        const bEvents = bGroups.flatMap((g) => eventsByGroup.get(g.id) ?? []);
        const liveStations = bGroups.reduce((sum, g) => sum + liveStationsForGroup(g), 0);
        const allStations = bGroups.reduce((sum, g) => sum + stationsForGroup(g), 0);
        const liveEvents = bEvents.reduce((n, e) => {
          const ls = liveStatus(e);
          return ls === 'Live now' || ls === 'Setup' ? n + 1 : n;
        }, 0);
        const lastEventTime = bEvents.reduce((max, e) => {
          const raw = e.endDate ?? e.startDate;
          if (!raw) return max;
          const t = new Date(raw).getTime();
          return Number.isFinite(t) && t > max ? t : max;
        }, 0);
        const completedEvents = bEvents.reduce((n, e) => {
          if (!e.endDate) return n;
          const end = new Date(e.endDate).getTime();
          return Number.isFinite(end) && end < now ? n + 1 : n;
        }, 0);
        return {
          brand,
          projects: bGroups.length,
          events: bEvents.length,
          liveEvents,
          activations: liveStations,
          spend: liveStations * SPEND_PER_LIVE_STATION,
          reachedYTD: allStations * REACH_PER_STATION_YTD,
          lastEventTime,
          completedEvents,
        };
      })
      .sort((a, b) => {
        if (b.liveEvents !== a.liveEvents) return b.liveEvents - a.liveEvents;
        if (b.lastEventTime !== a.lastEventTime) return b.lastEventTime - a.lastEventTime;
        if (b.completedEvents !== a.completedEvents) return b.completedEvents - a.completedEvents;
        return a.brand.name.localeCompare(b.brand.name);
      });
  }, [brandsQ.data, groupsQ.data, eventsQ.data, stationsQ.data]);

  const quarter = useMemo(() => {
    const d = new Date();
    return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
  }, []);

  const managersByBrand = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of managersQ.data?.items ?? []) {
      const name = `${m.firstname ?? ''} ${m.lastname ?? ''}`.trim() || m.email;
      for (const bid of m.managedBrandIds ?? []) {
        if (!map.has(bid)) map.set(bid, name);
      }
    }
    return map;
  }, [managersQ.data]);

  const loadingPortfolio =
    brandsQ.isLoading ||
    groupsQ.isLoading ||
    eventsQ.isLoading ||
    stationsQ.isLoading;

  // Nothing to summarise until the workspace has its first brand. Used to
  // suppress the summary blocks so the invitation is what the user meets.
  const isEmptyWorkspace = !loadingPortfolio && portfolio.length === 0;

  return (
    <div className="page">
      <header className="mb-6">
        <h1
          className="mb-1 text-2xl font-black tracking-tight text-[var(--color-xk-lavender)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {company.name}
        </h1>
        <p className="text-[13px] text-[var(--color-xk-text-muted)]">
          {portfolio.length >= 2
            ? `${t('dashboard.brandHub')} · ${portfolio.length} ${t('dashboard.brandsLower')}`
            : t('dashboard.portfolio')}{' '}
          · {quarter}
        </p>
      </header>

      {/* PRD Xotski v2.0 §7.1 — Entity Home top block.
       *  Atledi welcome hero · KPI strip (PRD lane-discipline metrics) ·
       *  My Next Event card. The legacy spend/reached rollup was removed
       *  because PRD §2 forbids Cost Per Moment / ad-equivalent value
       *  inside Xotski (those belong to Proxity / Xamplin). */}
      {/* Task 8.1 — an empty workspace gets an invitation, not a grid of
       *  blanks. The hero, KPI strip and next-event card all have nothing to
       *  say before the first brand exists, so they stay out of the way and
       *  the single primary action leads. */}
      {!isEmptyWorkspace && (
        <>
          <section className="mb-6">
            <AtlediWelcomeHero brandName={company.name} />
          </section>
          <section className="mb-6">
            <EntityKpiStrip />
          </section>
          <section className="mb-8">
            <MyNextEventCard brandColor={company.secondaryColor ?? undefined} />
          </section>
        </>
      )}


      <SubBrandsSection
        portfolio={portfolio}
        managersByBrand={managersByBrand}
        loading={loadingPortfolio}
        onCreate={() => {
          // Gate brand creation against the plan cap. brandsUsed is derived
          // from the live `/xk-company` listing (portfolio.length) because
          // co_company_plan_usage.brandsUsed is not maintained server-side.
          const limit = planUsage?.maxBrands ?? 0;
          if (portfolio.length >= limit) {
            setUpgradeOpen(true);
          } else {
            navigate('/brand-studio/new');
          }
        }}
        onOpen={(id) => navigate(`/brand/${id}`)}
      />
      <BrandManagersSection managersQ={managersQ} />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="maxBrands"
        currentPlanName="Free"
        limit={planUsage?.maxBrands ?? 0}
      />
    </div>
  );
}

// ─── Sub-brands section (paginated + exportable) ────────────────────────────

interface PortfolioEntry {
  brand: XkBrand;
  projects: number;
  events: number;
  liveEvents: number;
  activations: number;
  spend: number;
  reachedYTD: number;
}

function SubBrandsSection({
  portfolio,
  managersByBrand,
  loading,
  onCreate,
  onOpen,
}: {
  portfolio: PortfolioEntry[];
  managersByBrand: Map<number, string>;
  loading: boolean;
  onCreate: () => void;
  onOpen: (id: number) => void;
}) {
  const t = useT();
  const pager = usePagination(portfolio, 12);
  return (
    <Section
      title={t('dashboard.subBrands')}
      action={
        <div className="flex items-center gap-2">
          <ExportXlsxButton
            filename="sub-brands"
            sheetName="Sub-brands"
            disabled={portfolio.length === 0}
            rows={() =>
              portfolio.map((p) => portfolioEntryToExportRow(p, managersByBrand.get(p.brand.id)))
            }
          />
          <button
            type="button"
            onClick={onCreate}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-4 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)]"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('dashboard.newBrand')}
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : portfolio.length === 0 ? (
        <EmptyBrandsCard onCreate={onCreate} />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pager.pageItems.map((p) => (
              <SubBrandCard
                key={p.brand.id}
                brand={p.brand}
                projects={p.projects}
                events={p.events}
                liveEvents={p.liveEvents}
                activations={p.activations}
                spend={p.spend}
                manager={managersByBrand.get(p.brand.id)}
                onClick={() => onOpen(Number(p.brand.id))}
              />
            ))}
          </div>
          <Pagination {...pager} noun="brands" />
        </>
      )}
    </Section>
  );
}

function BrandManagersSection({
  managersQ,
}: {
  managersQ: { isLoading: boolean; data?: { items: User[] } };
}) {
  const t = useT();
  const items = managersQ.data?.items ?? [];
  const pager = usePagination(items, 12);
  return (
    <Section
      title={t('dashboard.brandManagers')}
      action={
        <ExportXlsxButton
          filename="brand-managers"
          sheetName="Brand managers"
          disabled={items.length === 0}
          rows={() => items.map(userToExportRow)}
        />
      }
    >
      {managersQ.isLoading ? (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-14 rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-xk-border)] p-6 text-center text-sm text-[var(--color-xk-text-muted)]">
          {t('dashboard.noManagers')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            {pager.pageItems.map((m) => (
              <ManagerRow key={m.id} user={m} />
            ))}
          </div>
          <Pagination {...pager} noun="managers" />
        </>
      )}
    </Section>
  );
}

/** Flatten a sub-brand row for XLSX export. */
function portfolioEntryToExportRow(p: PortfolioEntry, manager: string | undefined) {
  return {
    ID: p.brand.id,
    Name: p.brand.name,
    Industry: p.brand.industry ?? '',
    'Primary color': p.brand.backgroundColor ?? '',
    'Secondary color': p.brand.secondaryColor ?? '',
    Projects: p.projects,
    Events: p.events,
    'Live events': p.liveEvents,
    'Active stations': p.activations,
    Spend: p.spend,
    'People reached (YTD)': p.reachedYTD,
    Manager: manager ?? '',
    Status: p.brand.status ?? '',
  };
}

function userToExportRow(u: User) {
  return {
    ID: u.id,
    Name: `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim() || u.email,
    Email: u.email,
    Phone: u.phone ?? '',
    Title: u.title ?? '',
    'Managed brand IDs': (u.managedBrandIds ?? []).join(', '),
  };
}

// ─── Shared bits ────────────────────────────────────────────────────────────

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-8">
      <header className="mb-3 flex items-center justify-between">
        <h2
          className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {title}
        </h2>
        {action}
      </header>
      {children}
    </section>
  );
}

function SubBrandCard({
  brand,
  projects,
  events,
  liveEvents,
  activations,
  spend,
  manager,
  onClick,
}: {
  brand: XkBrand;
  projects: number;
  events: number;
  liveEvents: number;
  activations: number;
  spend: number;
  manager?: string;
  onClick: () => void;
}) {
  const t = useT();
  // Brand-identity palette (from BrandStudio):
  //   backgroundColor = primary, secondaryColor = accent, fontColor = text on brand
  const primary = brand.backgroundColor || null;
  const accent = brand.secondaryColor || 'var(--color-xk-accent)';
  const onPrimary = brand.fontColor || '#FFFFFF';
  const hasIdentity = Boolean(primary);
  const isLive = liveEvents > 0;
  return (
    // Outer wrapper is a div so the inner collapsible's trigger button isn't
    // nested inside another button. The header+stats area below is the click
    // target that navigates to the brand home.
    <div
      className="group relative block w-full overflow-hidden rounded-2xl border border-[var(--color-xk-border)] p-4 pl-5 text-left transition-all hover:-translate-y-0.5"
      style={{
        background: hasIdentity
          ? `linear-gradient(180deg, ${hexToRgba(primary as string, 0.1)} 0%, var(--color-xk-surface) 70%)`
          : 'var(--color-xk-surface)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = accent;
        e.currentTarget.style.boxShadow = `0 8px 24px -12px ${hexToRgba(accent, 0.5)}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Left identity rail — the only identity cue beyond the surface wash */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-1"
        style={{
          background: hasIdentity
            ? `linear-gradient(180deg, ${primary} 0%, ${accent} 100%)`
            : accent,
        }}
      />

      <button
        type="button"
        onClick={onClick}
        className="block w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-xk-accent)] rounded-md"
      >
      {/* Header — logo + name/subtitle inline, optional Live pill top-right */}
      <div className="mb-3 flex items-start gap-2.5">
        {brand.logo ? (
          <span
            className="grid h-9 w-9 flex-none place-items-center rounded-md"
            style={{
              backgroundColor: hasIdentity ? (primary as string) : 'transparent',
              border: `1px solid ${hexToRgba(accent, 0.5)}`,
            }}
          >
            <img src={resolveImageUrl(brand.logo)} alt={brand.name} className="h-7 w-7 object-contain" />
          </span>
        ) : (
          <span
            aria-hidden="true"
            className="grid h-9 w-9 flex-none place-items-center rounded-md text-sm font-black"
            style={{
              backgroundColor: hasIdentity ? (primary as string) : accent,
              color: hasIdentity ? onPrimary : 'var(--color-fg-on-accent)',
              border: `1px solid ${hexToRgba(accent, 0.6)}`,
              fontFamily: 'var(--font-display)',
            }}
          >
            {brand.name.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-[13px] font-extrabold leading-tight text-[var(--color-xk-lavender)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {brand.name}
          </p>
          {brand.industry && (
            <p className="mt-0.5 truncate text-[10px] text-[var(--color-xk-text-muted)]">
              {brand.industry}
            </p>
          )}
        </div>
        {isLive && <LiveDot accent={accent} count={liveEvents} />}
      </div>

      {/* Focal — spend + manager on one compact line */}
      <div className="mb-3 flex items-center gap-1.5 text-[10px] text-[var(--color-xk-text-muted)]">
        <DollarSign className="h-3 w-3 flex-none" />
        <span className="truncate">
          <span className="font-extrabold tabular-nums text-[var(--color-xk-lavender)]">
            ${spend.toLocaleString()}
          </span>
          {` ${t('subbrand.ytd')}`}
          {manager ? ` · ${manager}` : ''}
        </span>
      </div>

      {/* Pill chips */}
      <div className="flex flex-wrap gap-1.5">
        <StatPill label={t('subbrand.projects')} n={projects} />
        <StatPill label={t('subbrand.events')} n={events} />
        <StatPill label={t('subbrand.active')} n={activations} tint={isLive ? accent : undefined} />
      </div>
      </button>
      <div className="mt-3 border-t border-[var(--color-xk-border)] pt-2.5">
        <BrandTreeCollapsible brandId={brand.id} />
      </div>
    </div>
  );
}

function StatPill({ label, n, tint }: { label: string; n: number; tint?: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-xk-border)] bg-[rgb(0_0_0/0.18)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)]"
      style={{ fontFamily: 'var(--font-display)' }}
    >
      {label}
      <span
        className="font-extrabold tabular-nums"
        style={{ color: tint || 'var(--color-xk-lavender)' }}
      >
        {n}
      </span>
    </span>
  );
}

function LiveDot({ accent, count }: { accent: string; count: number }) {
  const t = useT();
  return (
    <span
      className="inline-flex flex-none items-center gap-1 rounded-full px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest"
      style={{
        backgroundColor: hexToRgba(accent, 0.16),
        color: accent,
      }}
    >
      <span
        aria-hidden="true"
        className="h-1 w-1 animate-pulse rounded-full"
        style={{ backgroundColor: accent }}
      />
      {t('common.live')} · {count}
    </span>
  );
}

/** #RRGGBB → rgba(r,g,b,a). Returns the input unchanged when it isn't a 6-char hex. */
function hexToRgba(hex: string, alpha: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ManagerRow({
  user,
}: {
  user: { id: number; firstname?: string; lastname?: string; email: string; title?: string | null };
}) {
  const fullName = `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim() || user.email;
  const initial = (user.firstname?.[0] ?? user.email[0] ?? '?').toUpperCase();
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5">
      <span
        aria-hidden="true"
        className="grid h-9 w-9 flex-none place-items-center rounded-full bg-[var(--color-xk-bg-deep)] text-xs font-extrabold text-[var(--color-xk-lavender)]"
      >
        {initial}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-bold text-[var(--color-xk-lavender)]">
          {fullName}
        </div>
        <div className="truncate text-[11px] text-[var(--color-xk-text-muted)]">
          {user.title ?? user.email}
        </div>
      </div>
    </div>
  );
}

function EmptyBrandsCard({ onCreate }: { onCreate: () => void }) {
  const t = useT();
  return (
    <div className="rounded-2xl border-2 border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-16 text-center">
      <h3
        className="mb-2 text-lg font-black text-[var(--color-xk-lavender)]"
        style={{ fontFamily: 'var(--font-display)' }}
      >
        {t('dashboard.noBrandsTitle')}
      </h3>
      <p className="mb-5 text-sm text-[var(--color-xk-text-muted)]">
        {t('dashboard.noBrandsBody')}
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-6 py-3 text-sm font-extrabold text-[var(--color-fg-on-accent)] hover:bg-[var(--color-xk-accent-hover)]"
      >
        <Plus className="h-4 w-4" />
        {t('dashboard.newBrand')}
      </button>
    </div>
  );
}
